import { useMemo, useState } from "react";
import { FolderTree, Plus } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError } from "../lib/api";
import { num } from "../lib/format";
import { showQueryLoader } from "../lib/query-utils";
import { toast } from "../stores/toast";
import { useAuth } from "../stores/auth";
import { useBottomBarActions } from "../hooks/useBottomBarActions";
import { Button, Input, SearchSelect, useConfirm } from "../components/ui";
import { DataTable, type Column } from "../components/DataTable";
import { ListPageShell } from "../components/shell/ListPageShell";
import { RightSidebarPanel } from "../components/RightSidebarPanel";
import { ImageUploadField } from "../components/ImageUploadField";
import { categoryPathMap, categoryPathOptions } from "../lib/categoryPaths";

interface CategoryRow {
  id: string;
  name: string;
  slug: string;
  parentId: string | null;
  sortOrder: number;
  productCount: number;
  cardBackingImageUrl: string | null;
}

interface CategoryForm {
  name: string;
  parentId: string;
  sortOrder: string;
  cardBackingImageUrl: string | null;
}

function emptyForm(): CategoryForm {
  return { name: "", parentId: "", sortOrder: "0", cardBackingImageUrl: null };
}

function rowToForm(row: CategoryRow): CategoryForm {
  return {
    name: row.name,
    parentId: row.parentId ?? "",
    sortOrder: String(row.sortOrder),
    cardBackingImageUrl: row.cardBackingImageUrl,
  };
}

function descendantIds(categories: CategoryRow[], rootId: string): Set<string> {
  const ids = new Set([rootId]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const c of categories) {
      if (c.parentId && ids.has(c.parentId) && !ids.has(c.id)) {
        ids.add(c.id);
        grew = true;
      }
    }
  }
  return ids;
}

export function Categories() {
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const { confirm, dialog } = useConfirm();

  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<CategoryRow | null>(null);
  const [form, setForm] = useState<CategoryForm>(emptyForm());

  const canWrite = can("catalog.write");

  const listQuery = useQuery({
    queryKey: ["catalog-categories"],
    queryFn: () => api<{ categories: CategoryRow[] }>("/catalog/categories"),
  });

  const categories = useMemo(() => listQuery.data?.categories ?? [], [listQuery.data?.categories]);
  const paths = useMemo(() => categoryPathMap(categories), [categories]);
  const parentPath = (parentId: string | null) => (parentId ? paths.get(parentId) ?? "—" : "—");

  const saveMutation = useMutation({
    mutationFn: (payload: { id?: string; body: Record<string, unknown> }) => {
      if (payload.id) {
        return api(`/catalog/categories/${payload.id}`, { method: "PUT", body: payload.body });
      }
      return api("/catalog/categories", { method: "POST", body: payload.body });
    },
    onSuccess: () => {
      toast("success", editing ? "Category updated" : "Category created");
      closeEditor();
      void queryClient.invalidateQueries({ queryKey: ["catalog-categories"] });
    },
    onError: (err) => toast("error", err instanceof ApiError ? err.message : "Save failed"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api(`/catalog/categories/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      closeEditor();
      void queryClient.invalidateQueries({ queryKey: ["catalog-categories"] });
    },
    onError: (err) => toast("error", err instanceof ApiError ? err.message : "Delete failed"),
  });

  function closeEditor() {
    setEditorOpen(false);
    setEditing(null);
    setForm(emptyForm());
  }

  function openCreate() {
    setEditing(null);
    setForm(emptyForm());
    setEditorOpen(true);
  }

  function openEdit(row: CategoryRow) {
    setEditing(row);
    setForm(rowToForm(row));
    setEditorOpen(true);
  }

  function openDuplicate(row: CategoryRow) {
    setEditing(null);
    setForm({ ...rowToForm(row), name: `${row.name} (copy)` });
    setEditorOpen(true);
  }

  const tableRowActions = useMemo(
    () =>
      canWrite
        ? {
            onEdit: openEdit,
            onDuplicate: openDuplicate,
            onDelete: async (row: CategoryRow) => {
              await deleteMutation.mutateAsync(row.id);
            },
          }
        : undefined,
    [canWrite, deleteMutation],
  );

  const tableSelection = useMemo(
    () =>
      canWrite
        ? {
            entityLabel: "Category",
            onEdit: (rows: CategoryRow[]) => openEdit(rows[0]!),
            onDuplicate: (rows: CategoryRow[]) => rows.forEach(openDuplicate),
            onDelete: async (rows: CategoryRow[]) => {
              await Promise.all(rows.map((r) => deleteMutation.mutateAsync(r.id)));
            },
          }
        : undefined,
    [canWrite, deleteMutation],
  );

  function submitCategory() {
    saveMutation.mutate({
      id: editing?.id,
      body: {
        name: form.name.trim(),
        parentId: form.parentId || null,
        sortOrder: Number.parseInt(form.sortOrder, 10) || 0,
      },
    });
  }

  useBottomBarActions(() => {
    if (!canWrite) return [];
    if (editorOpen) {
      return [
        { id: "cancel", label: "Cancel", onClick: closeEditor },
        {
          id: "save",
          label: "Save",
          variant: "primary" as const,
          disabled: saveMutation.isPending || form.name.trim().length < 2,
          onClick: submitCategory,
        },
      ];
    }
    return [{ id: "create", label: "Create", icon: Plus, variant: "primary" as const, onClick: openCreate }];
  });

  const sortedRows = useMemo(() => {
    return [...categories].sort((a, b) => {
      if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
      return a.name.localeCompare(b.name);
    });
  }, [categories]);

  const columns: Column<CategoryRow>[] = useMemo(
    () => [
      {
        key: "name",
        header: "Name",
        sortable: true,
        sortValue: (r) => r.name,
        exportValue: (r) => `${paths.get(r.id) ?? r.name} (${r.slug})`,
        render: (r) => (
          <div>
            <p className="font-medium">{r.name}</p>
            <p className="font-mono text-[10px] text-muted-2">{r.slug}</p>
          </div>
        ),
      },
      {
        key: "parent",
        header: "Parent",
        hideSm: true,
        sortable: true,
        sortValue: (r) => parentPath(r.parentId),
        render: (r) => <span className="text-xs text-muted">{parentPath(r.parentId)}</span>,
      },
      {
        key: "sort",
        header: "Sort",
        className: "text-right",
        sortable: true,
        sortValue: (r) => r.sortOrder,
        render: (r) => <span className="text-xs">{num(r.sortOrder)}</span>,
      },
      {
        key: "products",
        header: "Products",
        className: "text-right",
        sortable: true,
        sortValue: (r) => r.productCount,
        render: (r) => <span className="text-xs">{num(r.productCount)}</span>,
      },
    ],
    [paths],
  );

  const parentSelectOptions = useMemo(() => {
    const excluded = editing ? descendantIds(categories, editing.id) : new Set<string>();
    return [
      { value: "", label: "None (top level)" },
      ...categoryPathOptions(categories.filter((c) => !excluded.has(c.id))).map((c) => ({
        value: c.id,
        label: c.label,
      })),
    ];
  }, [categories, editing]);

  return (
    <ListPageShell>
      <DataTable
          columns={columns}
          rows={sortedRows}
          rowKey={(r) => r.id}
          loading={showQueryLoader(listQuery)}
          exportFilename="categories"
          onRowClick={canWrite ? openEdit : undefined}
          entityLabel="Category"
          rowActions={tableRowActions}
          selection={tableSelection}
          empty={{
            icon: FolderTree,
            title: "No categories yet",
            action: canWrite ? (
              <Button size="sm" onClick={openCreate}>
                Create
              </Button>
            ) : undefined,
          }}
        />

      <RightSidebarPanel
        open={editorOpen}
        title={editing ? "Edit category" : "Create category"}
        subtitle={editing?.slug}
        onClose={closeEditor}
        footer={
          canWrite && (
            <div className="flex flex-wrap justify-end gap-2">
              {editing && (
                <Button
                  variant="danger"
                  size="sm"
                  disabled={deleteMutation.isPending || editing.productCount > 0}
                  onClick={() =>
                    confirm({
                      title: "Delete category?",
                      message: `Remove ${editing.name}?`,
                      confirmLabel: "Delete",
                      danger: true,
                      action: async () => {
                        await deleteMutation.mutateAsync(editing.id);
                        toast("success", "Category deleted");
                      },
                    })
                  }
                >
                  Delete
                </Button>
              )}
              <Button variant="ghost" size="sm" onClick={closeEditor}>
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={saveMutation.isPending || form.name.trim().length < 2}
                onClick={() =>
                  saveMutation.mutate({
                    id: editing?.id,
                    body: {
                      name: form.name.trim(),
                      parentId: form.parentId || null,
                      sortOrder: Number.parseInt(form.sortOrder, 10) || 0,
                      cardBackingImageUrl: form.cardBackingImageUrl,
                    },
                  })
                }
              >
                Save
              </Button>
            </div>
          )
        }
      >
        <div className="sidebar-form">
          <Input
            label="Name"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            hint="Must be unique within the parent — the same name can live under different parents"
          />
          <SearchSelect
            label="Parent category"
            options={parentSelectOptions}
            value={form.parentId}
            onChange={(v) => setForm((f) => ({ ...f, parentId: v }))}
          />
          <Input
            label="Sort order"
            type="number"
            value={form.sortOrder}
            onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
            hint="Lower numbers appear first"
          />
          <ImageUploadField
            label="Card backing image"
            hint="Default placeholder for products in this category when they have no image."
            value={form.cardBackingImageUrl ? [form.cardBackingImageUrl] : []}
            onChange={(urls) => setForm((f) => ({ ...f, cardBackingImageUrl: urls[0] ?? null }))}
            max={1}
          />
          {editing && (
            <div className="rounded-xl border border-line bg-white/[0.02] px-3 py-2 text-xs">
              <p className="text-white/40">Slug</p>
              <p className="font-mono">{editing.slug}</p>
            </div>
          )}
        </div>
      </RightSidebarPanel>

      {dialog}
    </ListPageShell>
  );
}
